import React from "react";
import ReactMarkdown from "react-markdown";
import {
  Loader,
  MapPin,
  BarChart3,
  AlertTriangle,
  CloudRainWind,
  Leaf,
  CheckCircle2,
  ShieldAlert,
  Activity,
  Sprout,
} from "lucide-react";

export interface AIInterpretationData {
  title?: string; // Optional title
  risks?: string[]; // List of identified risks/stressors
  impact?: string; // Narrative about yield/general impact
  adaptation?: string[]; // List of adaptation strategies
}

interface AIInterpretationDisplayProps {
  interpretation?: string;
  data?: AIInterpretationData | null;
  isLoading?: boolean;
  error?: string | null;
  className?: string;
}

/**
 * Reusable component to display formatted AI interpretations
 * Supports both Markdown string and structured JSON data
 */
const AIInterpretationDisplay: React.FC<AIInterpretationDisplayProps> = ({
  interpretation,
  data,
  isLoading = false,
  error = null,
  className = "",
}) => {
  const extractText = (children: React.ReactNode): string => {
    if (typeof children === "string") return children;
    if (Array.isArray(children)) return children.map(extractText).join("");
    if (React.isValidElement(children))
      return extractText(children.props.children);
    return "";
  };

  const sectionMeta = (title: string) => {
    const t = (title || "").toLowerCase();

    // Mapping for new simplified sections
    if (t.includes("risks") || t.includes("risk")) {
      return { color: "#ef4444", Icon: ShieldAlert };
    }
    if (t.includes("impact")) {
      return { color: "#f59e0b", Icon: Activity };
    }
    if (t.includes("adaptation")) {
      return { color: "#22c55e", Icon: Sprout };
    }

    // Fallback/Legacy mappings
    if (
      t.includes("📍") ||
      t.includes("general overview") ||
      t.includes("summary")
    ) {
      return { color: "#14b8a6", Icon: MapPin };
    }
    if (
      t.includes("🌽") ||
      t.includes("expected impact") ||
      t.includes("trends")
    ) {
      return { color: "#f59e0b", Icon: BarChart3 };
    }
    if (t.includes("⚠️") || t.includes("identified climate risks")) {
      return { color: "#ef4444", Icon: AlertTriangle };
    }
    if (t.includes("🌦️") || t.includes("dominant climate stressors")) {
      return { color: "#4f46e5", Icon: CloudRainWind };
    }
    if (
      t.includes("🌱") ||
      t.includes("recommended adaptation") ||
      t.includes("recommendations")
    ) {
      return { color: "#22c55e", Icon: Leaf };
    }
    if (
      t.includes("✅") ||
      t.includes("key takeaway") ||
      t.includes("confidence")
    ) {
      return { color: "#3b82f6", Icon: CheckCircle2 };
    }
    return { color: "#3b82f6", Icon: CheckCircle2 };
  };

  if (error) {
    return (
      <div
        className={`rounded-lg bg-red-50 p-4 border border-red-200 ${className}`}
      >
        <div className="flex items-center text-red-700">
          <span className="font-medium">AI Analysis Error:</span>
          <span className="ml-2">{error}</span>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div
        className={`flex flex-row items-center justify-center py-4 bg-blue-50 rounded-lg border border-blue-200 ${className}`}
      >
        <Loader className="animate-spin text-blue-600 mr-2" size={20} />
        <span className="text-blue-700 font-medium">
          AI Analysis in progress...
        </span>
      </div>
    );
  }

  // Render Structured Data if available
  if (data) {
    // Helper for rendering section header style (matching Markdown fallback)
    const renderHeader = (
      title: string,
      color: string,
      Icon: React.ElementType,
    ) => (
      <div
        className="mb-3 rounded-lg"
        style={{
          borderLeft: `4px solid ${color}`,
          background: `${color}26`, // 15% opacity hex
          padding: "0.5rem 0.75rem",
        }}
      >
        <div className="flex items-center gap-2">
          <Icon style={{ color }} width={20} height={20} />
          <h2 className="text-lg font-semibold" style={{ color, margin: 0 }}>
            {title}
          </h2>
        </div>
      </div>
    );

    const risksMeta = sectionMeta("Risks");
    const impactMeta = sectionMeta("Impact");
    const adaptationMeta = sectionMeta("Adaptation");

    return (
      <div
        className={`rounded-lg p-4 ${className}`}
        style={{ background: "#ffffff", border: "1px solid #e5e7eb" }}
      >
        {/* Risks Section */}
        {data.risks && data.risks.length > 0 && (
          <section className="mb-6">
            {renderHeader("Risks", risksMeta.color, risksMeta.Icon)}
            <div
              className="p-3 rounded-md"
              style={{ background: `${risksMeta.color}15` }} // 8% opacity hex
            >
              <ol
                className="space-y-0.5 list-decimal pl-5"
                style={{ color: "#374151" }}
              >
                {data.risks.map((risk, idx) => (
                  <li key={idx} className="list-item leading-snug">
                    {risk}
                  </li>
                ))}
              </ol>
            </div>
          </section>
        )}

        {/* Impact Section */}
        {data.impact && (
          <section className="mb-6">
            {renderHeader("Impact", impactMeta.color, impactMeta.Icon)}
            <div
              className="p-3 rounded-md"
              style={{ background: `${impactMeta.color}15` }} // 8% opacity hex
            >
              <p
                className="mb-0 leading-snug whitespace-pre-line"
                style={{ color: "#374151" }}
              >
                {data.impact}
              </p>
            </div>
          </section>
        )}

        {/* Adaptation Section */}
        {data.adaptation && data.adaptation.length > 0 && (
          <section className="mb-2">
            {renderHeader(
              "Adaptation",
              adaptationMeta.color,
              adaptationMeta.Icon,
            )}
            <div
              className="p-3 rounded-md"
              style={{ background: `${adaptationMeta.color}15` }} // 8% opacity hex
            >
              <ol
                className="space-y-0.5 list-decimal pl-5"
                style={{ color: "#374151" }}
              >
                {data.adaptation.map((rec, idx) => (
                  <li key={idx} className="list-item leading-snug">
                    {rec}
                  </li>
                ))}
              </ol>
            </div>
          </section>
        )}
      </div>
    );
  }

  // Fallback to Markdown rendering
  const rawText =
    interpretation ||
    "*Analysis in progress... Please wait while AI processes the climate data.*";

  // Check if the text seems to follow the "## Title" structure
  const hasSections = rawText.includes("##");

  if (hasSections) {
    const parts = rawText.split(/##\s+/).filter((s) => s.trim().length > 0);

    return (
      <div
        className={`rounded-lg p-4 ${className}`}
        style={{ background: "#ffffff", border: "1px solid #e5e7eb" }}
      >
        {parts.map((part, idx) => {
          const newlineIdx = part.indexOf("\n");
          let title = "";
          let content = part;

          if (newlineIdx !== -1) {
            title = part.substring(0, newlineIdx).trim();
            content = part.substring(newlineIdx).trim();
          } else {
            title = part.trim();
            content = "";
          }

          const { color, Icon } = sectionMeta(title);

          return (
            <section key={idx} className="mb-6 last:mb-0">
              {/* Header */}
              <div
                className="mb-3 rounded-lg"
                style={{
                  borderLeft: `4px solid ${color}`,
                  background: `${color}26`,
                  padding: "0.5rem 0.75rem",
                }}
              >
                <div className="flex items-center gap-2">
                  <Icon style={{ color }} width={20} height={20} />
                  <h2
                    className="text-lg font-semibold"
                    style={{ color, margin: 0 }}
                  >
                    {title}
                  </h2>
                </div>
              </div>

              {/* Content with background */}
              <div
                className="p-3 rounded-md"
                style={{ background: `${color}15` }}
              >
                <div className="prose prose-sm max-w-none">
                  <ReactMarkdown
                    components={{
                      h3: ({ children }) => (
                        <h3
                          className="text-md font-medium mb-1 mt-2"
                          style={{ color: "#1f2937" }}
                        >
                          {children}
                        </h3>
                      ),
                      p: ({ children }) => (
                        <p
                          className="mb-1.5 leading-snug"
                          style={{ color: "#374151" }}
                        >
                          {children}
                        </p>
                      ),
                      ol: ({ children }) => (
                        <ol
                          className="mb-1.5 space-y-0.5 list-decimal pl-5"
                          style={{ color: "#374151" }}
                        >
                          {children}
                        </ol>
                      ),
                      ul: ({ children }) => (
                        <ol
                          className="mb-1.5 space-y-0.5 list-decimal pl-5"
                          style={{ color: "#374151" }}
                        >
                          {children}
                        </ol>
                      ),
                      li: ({ children }) => (
                        <li
                          className="list-item leading-snug"
                          style={{ color: "#374151" }}
                        >
                          {children}
                        </li>
                      ),
                      strong: ({ children }) => (
                        <strong
                          className="font-semibold"
                          style={{ color: "#111827" }}
                        >
                          {children}
                        </strong>
                      ),
                    }}
                  >
                    {content}
                  </ReactMarkdown>
                </div>
              </div>
            </section>
          );
        })}
      </div>
    );
  }

  // Legacy/Simple Fallback (no sections detected)
  return (
    <div
      className={`rounded-lg p-4 ${className}`}
      style={{ background: "#ffffff", border: "1px solid #e5e7eb" }}
    >
      <div className="prose prose-sm max-w-none">
        <ReactMarkdown
          components={{
            h2: ({ children }) => {
              const title = extractText(children);
              const { color, Icon } = sectionMeta(title);
              return (
                <div
                  className="mt-3 first:mt-0 mb-1 rounded-lg"
                  style={{
                    borderLeft: `4px solid ${color}`,
                    background: `${color}26`,
                    padding: "0.5rem 0.75rem",
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Icon style={{ color }} width={20} height={20} />
                    <h2
                      className="text-lg font-semibold"
                      style={{ color, margin: 0 }}
                    >
                      {children}
                    </h2>
                  </div>
                </div>
              );
            },
            h3: ({ children }) => (
              <h3
                className="text-md font-medium mb-1 mt-2"
                style={{ color: "#1f2937" }}
              >
                {children}
              </h3>
            ),
            p: ({ children }) => (
              <p className="mb-1 leading-snug" style={{ color: "#374151" }}>
                {children}
              </p>
            ),
            ol: ({ children }) => (
              <ol
                className="mb-1 space-y-0.5 list-decimal pl-5"
                style={{ color: "#374151" }}
              >
                {children}
              </ol>
            ),
            ul: ({ children }) => (
              <ol
                className="mb-1 space-y-0.5 list-decimal pl-5"
                style={{ color: "#374151" }}
              >
                {children}
              </ol>
            ),
            li: ({ children }) => (
              <li
                className="list-item leading-snug"
                style={{ color: "#374151" }}
              >
                {children}
              </li>
            ),
            strong: ({ children }) => (
              <strong className="font-semibold" style={{ color: "#111827" }}>
                {children}
              </strong>
            ),
            hr: () => (
              <hr className="my-4" style={{ borderColor: "#e5e7eb" }} />
            ),
            blockquote: ({ children }) => (
              <blockquote
                className="border-l-4 pl-4 italic my-3"
                style={{ borderColor: "#d1d5db", color: "#374151" }}
              >
                {children}
              </blockquote>
            ),
            code: ({ children }) => (
              <code
                className="px-1 py-0.5 rounded text-sm"
                style={{ background: "#f3f4f6", color: "#1f2937" }}
              >
                {children}
              </code>
            ),
          }}
        >
          {interpretation ||
            "*Analysis in progress... Please wait while AI processes the climate data.*"}
        </ReactMarkdown>
      </div>
    </div>
  );
};

export default AIInterpretationDisplay;
